import React, { useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { BrowserRouter as Router, Routes, Route, useSearchParams } from "react-router-dom";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "./Assets/Translation/i18n";
import useGoogleAnalytics from "./Hooks/useGoogleAnalytics";

import MainPage from "./Pages/MainPage";
import ToolboxPage from "./Pages/ToolboxPage";
import BlogsPage from "./Pages/BlogsPage";
import BlogPost from "./Components/BlogPost/BlogPost";
import FloatingHeader from "./Components/FloatingHeader/FloatingHeader";
import { ModalProvider, useModal } from './Hooks/useModal';
import ContactModal from "./Components/ContactModal/ContactModal";
import BackToTop from './Components/BackToTop/BackToTop';
import ScrollToTop from "./Components/ScrollToTop/ScrollToTop";
import ReactLenis from "lenis/react";

// Router content that needs to access router hooks
const RouterContent = () => {
  const { isModalOpen, setIsModalOpen } = useModal();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  // Initialize Google Analytics tracking
  useGoogleAnalytics();

  useEffect(() => {
    // Check if the URL has ?form parameter and open the modal if it does
    if (searchParams.has('form')) {
      setIsModalOpen(true);
    }
  }, [searchParams, setIsModalOpen]);

  useEffect(() => {
    // Function to get the browser language
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      return browserLang.split('-')[0]; // Get the primary language code
    };

    // Set the initial language based on browser settings
    const browserLang = getBrowserLanguage();
    if (i18n.languages.includes(browserLang)) {
      i18n.changeLanguage(browserLang);
    } else {
      i18n.changeLanguage('en'); // Fallback to English if browser language is not supported
    }
  }, [i18n]);

  useEffect(() => {
    // Set the lang attribute on the html tag
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          style: {
            background: "#fff",
            color: "#121212",
          },
        }}
      />
      {!searchParams.has('bubblesview') && <FloatingHeader />}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/toolbox" element={<ToolboxPage />} />
        <Route path="*" element={<MainPage />} />
      </Routes>
      {!searchParams.has('bubblesview') && <BackToTop />}
    </>
  );
};

// New AppContent component that wraps RouterContent with Router
const AppContent = () => {
  const lenisRef = useRef(null)
  return (
    <ReactLenis
      root="asChild"
      ref={lenisRef}
    >
      <Router>
        <RouterContent />
      </Router>
    </ReactLenis>
  );
};

export default function App() {
  document.documentElement.style.setProperty("--background-color", "#FFFFFF");
  document.documentElement.style.setProperty("--text-color", "#000000");
  document.documentElement.style.setProperty("--foreground-color", "#f5f5f5");
  document.documentElement.style.setProperty("--border-color", "#00000035");
  document.documentElement.style.setProperty("--accent-color", "#00000010");
  document.documentElement.style.setProperty("--blurCardColor", "#ffffffA0");

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ModalProvider>
          <AppContent />
        </ModalProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
}